import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useTenant } from '@/services/tenant-context';
import { contractsApi } from '@/services/contracts-api';
import type { KoshContractStatus } from '@/types/contracts';

function useScope(): string {
  return useTenant().currentTenant.id;
}

export function useContracts(params?: Record<string, string | number | undefined>) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'contracts', scope, params ?? {}],
    queryFn: () => contractsApi.list({ pageSize: 200, ...params }),
  });
}

export function useContract(id: string | undefined) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'contracts', scope, id],
    queryFn: () => contractsApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useContractMilestones(id: string | undefined) {
  const scope = useScope();
  return useQuery({
    queryKey: ['kosh', 'contract-milestones', scope, id],
    queryFn: () => contractsApi.milestones(id!),
    enabled: Boolean(id),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return (keys: QueryKey[]) => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
}

export function useContractTransition() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: KoshContractStatus }) =>
      contractsApi.transition(id, to),
    onSuccess: () => invalidate([['kosh', 'contracts']]),
  });
}

export function useAcceptMilestone() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, mid }: { id: string; mid: string }) =>
      contractsApi.acceptMilestone(id, mid),
    onSuccess: () => invalidate([['kosh', 'contract-milestones']]),
  });
}

export function useBillMilestone() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, mid }: { id: string; mid: string }) =>
      contractsApi.billMilestone(id, mid),
    onSuccess: () =>
      invalidate([
        ['kosh', 'contract-milestones'],
        ['kosh', 'invoices'],
        ['kosh', 'outstandings'],
      ]),
  });
}
